import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "./api";
import { EmptyMark, LoadingState } from "./ParticipantShell";
import { createIdempotencyKey, waitingPoolErrorMessage } from "../shared/waiting-pool-client";
import type { CountrySearchResult, LocationSearchResult } from "./types";
import type {
  BeforeWeMeetAnswer,
  CandidateEnvelope,
  ConnectionEntry,
  ContactChannel,
  ContactEntry,
  CurrentCandidate,
  HistoryEntry,
  IncomingIntent,
  PairingCapabilityMode,
  PairingSyncResult,
  PairingStatus,
  ResultScopeRecord,
  ResultLocationScope,
} from "./pairing-types";

type Surface = "current" | "incoming" | "status" | "history" | "connections";
type Confirmation = { kind: "dismiss" | "willing" | "withdraw" | "retry" | "delete-contact"; counterpartId?: string; entry?: ContactEntry };

export const AGE_LABELS: Record<string, string> = {
  "18_24": "18–24", "25_29": "25–29", "30_34": "30–34", "35_39": "35–39",
  "40_49": "40–49", "50_59": "50–59", "60_plus": "60+",
};

export const BEFORE_WE_MEET_PROMPTS = [
  "如果我们第一次见面，我会比较喜欢……",
  "如果一起出去，我会想去…… / 做……",
  "最近我很愿意聊……",
  "如果一起吃东西，我会比较喜欢……",
  "如果你不知道怎么和我开始聊天，可以从……开始。",
  "第一次认识我的时候，有什么小事提前知道会比较好？",
  "还有什么你想提前留给未来会认识你的人？",
] as const;

export const CHANNEL_LABELS: Record<ContactChannel, string> = {
  wechat: "微信", email: "邮箱", telegram: "Telegram", instagram: "Instagram", other: "其他",
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return waitingPoolErrorMessage(error.code, error.message);
  return error instanceof Error ? error.message : "请求未能完成";
}

function dateTime(value: string | null): string {
  if (!value) return "";
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function remaining(value: string, now = Date.now()): string {
  const milliseconds = Math.max(0, Date.parse(value) - now);
  const totalMinutes = Math.ceil(milliseconds / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `约 ${hours} 小时 ${minutes} 分钟` : `约 ${minutes} 分钟`;
}

/** The viewer's own 24-hour period as a track: elapsed share, time left, and when it will be sent. */
function ConsiderationClock({ pendingAt, maturesAt, now }: { pendingAt: string; maturesAt: string; now: number }) {
  const start = Date.parse(pendingAt);
  const end = Date.parse(maturesAt);
  const share = end > start ? Math.min(1, Math.max(0, (now - start) / (end - start))) : 1;
  return <div className="r4-clock">
    <div className="r4-clock-track" aria-hidden="true"><span style={{ width: `${Math.max(4, share * 100)}%` }} /></div>
    <p className="r4-clock-labels"><span>还有<strong>{remaining(maturesAt, now)}</strong></span><span>{dateTime(maturesAt)} 送出</span></p>
  </div>;
}

export function CandidateProfile({ envelope }: { envelope: CandidateEnvelope }) {
  const profile = envelope.profile;
  return <div className="encounter-profile">
    <div className="encounter-heading">
      <div><h3>{profile.displayName}</h3></div>
      <p className="encounter-meta">{AGE_LABELS[profile.ageBand]}{profile.pronouns ? ` · ${profile.pronouns}` : ""}</p>
    </div>
    {profile.customGenderDisplayLabel && <p className="quiet-line">{profile.customGenderDisplayLabel}</p>}
    <p className="encounter-introduction">{profile.shortIntroduction}</p>
    {profile.beforeYouMeetMeText && <div className="before-meet"><h4>Before We Meet</h4><p>{profile.beforeYouMeetMeText}</p></div>}
  </div>;
}

type PresentationOutcome = "recorded" | "stale" | "retry";

function PresentedCandidate({ item, onPresented, children }: {
  item: CurrentCandidate | IncomingIntent;
  onPresented: (counterpartId: string, idempotencyKey: string) => Promise<PresentationOutcome>;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const idempotencyKey = useRef(createIdempotencyKey());
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    let timer: number | null = null;
    let visible = false;
    let stopped = false;
    const schedule = (delay: number) => {
      if (stopped || !visible || timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        void onPresented(item.counterpartId, idempotencyKey.current).then((outcome) => {
          if (outcome === "recorded" || outcome === "stale") {
            stopped = true;
            observer.disconnect();
          } else {
            schedule(800);
          }
        });
      }, delay);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      if (visible) {
        schedule(400);
      } else if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [0.5] });
    observer.observe(node);
    return () => { stopped = true; observer.disconnect(); if (timer !== null) window.clearTimeout(timer); };
  }, [item.counterpartId, onPresented]);
  return <article ref={ref} className="encounter-sheet">{children}</article>;
}

function EmptySurface({ children }: { children: React.ReactNode }) {
  return <div className="pairing-empty"><EmptyMark /><h2>{children}</h2></div>;
}

export function ResultScopeEditor({ onSaved, focusOnLoad = false }: { onSaved: () => Promise<boolean>; focusOnLoad?: boolean }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focused = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [record, setRecord] = useState<ResultScopeRecord | null>(null);
  const [kind, setKind] = useState<ResultLocationScope["kind"]>("same_city");
  const [country, setCountry] = useState<CountrySearchResult | null>(null);
  const [cities, setCities] = useState<LocationSearchResult[]>([]);
  const [countryQuery, setCountryQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [countryOptions, setCountryOptions] = useState<CountrySearchResult[]>([]);
  const [cityOptions, setCityOptions] = useState<LocationSearchResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const result = await api<{ resultLocationScope: ResultScopeRecord | null }>("/api/v1/pairing/result-location-scope");
    const next = result.resultLocationScope;
    setRecord(next);
    setLoaded(true);
    if (!next) return;
    setKind(next.scope.kind);
    setCities(next.display.cities);
    const selectedCountry = next.display.country ?? (next.display.cities[0] ? {
      countryRegionCode: next.display.cities[0].countryRegionCode,
      displayName: next.display.cities[0].countryDisplayName,
    } : null);
    setCountry(selectedCountry);
    setCountryQuery(selectedCountry?.displayName ?? "");
    setCityQuery("");
  }, []);
  useEffect(() => { void load().catch((caught) => setError(errorMessage(caught))); }, [load]);
  // Arriving from Explore's settings button lands on this section, once, after it has content.
  useEffect(() => {
    if (!focusOnLoad || !loaded || focused.current) return;
    focused.current = true;
    // Scroll only the screen's own scroller: scrollIntoView also moves every ancestor, and on iOS that
    // once carried the whole app (and its Back button) out of reach.
    requestAnimationFrame(() => {
      const heading = headingRef.current;
      const scroller = heading?.closest<HTMLElement>(".r4-body");
      if (heading && scroller) scroller.scrollTo({ top: scroller.scrollTop + heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 16 });
      heading?.focus({ preventScroll: true });
    });
  }, [focusOnLoad, loaded]);
  useEffect(() => {
    if ((kind !== "country_region" && kind !== "cities") || countryQuery.trim().length < 1) return setCountryOptions([]);
    const timer = window.setTimeout(() => void api<{ results: CountrySearchResult[] }>(`/api/v1/locations/countries?locale=zh-CN&q=${encodeURIComponent(countryQuery)}`)
      .then((result) => setCountryOptions(result.results)).catch(() => setCountryOptions([])), 180);
    return () => window.clearTimeout(timer);
  }, [countryQuery, kind]);
  useEffect(() => {
    if (kind !== "cities" || !country || cityQuery.trim().length < 1) return setCityOptions([]);
    const timer = window.setTimeout(() => void api<{ results: LocationSearchResult[] }>(`/api/v1/locations/search?locale=zh-CN&countryRegionCode=${country.countryRegionCode}&q=${encodeURIComponent(cityQuery)}`)
      .then((result) => setCityOptions(result.results)).catch(() => setCityOptions([])), 200);
    return () => window.clearTimeout(timer);
  }, [cityQuery, country, kind]);

  const save = async () => {
    const scope: ResultLocationScope = kind === "country_region"
      ? { kind, countryRegionCode: country?.countryRegionCode ?? "" }
      : kind === "cities" ? { kind, placeIds: cities.map(({ placeId }) => placeId) } : { kind };
    setBusy(true); setError(""); setMessage("");
    try {
      await api("/api/v1/pairing/result-location-scope", {
        method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() },
        body: JSON.stringify({ expectedRevision: record?.revision ?? null, scope }),
      });
      await load();
      const refreshed = await onSaved();
      setMessage(refreshed
        ? "范围已保存，系统不会自动扩大。"
        : "范围已保存，暂时无法刷新人选，请稍后重试。");
    } catch (caught) { setError(errorMessage(caught)); await load().catch(() => undefined); }
    finally { setBusy(false); }
  };

  return <section className="connection-subsection" aria-labelledby="result-scope-heading">
    <h3 id="result-scope-heading" ref={headingRef} tabIndex={-1}>希望认识哪里的人</h3>
    <p className="section-intro">这里决定 Now 里的人选来自哪里，和「哪些地方的人可以看到我」分开设置。范围不会被自动扩大。</p>
    <div className="scope-choice-grid">
      {([ ["same_city", "同城"], ["same_country_region", "同一国家或地区"], ["global", "全球"], ["country_region", "指定国家或地区"], ["cities", "指定城市"] ] as const).map(([value, label]) =>
        <label className="option-row" key={value}><input type="radio" name="result-scope" checked={kind === value} onChange={() => setKind(value)} /><span>{label}</span></label>)}
    </div>
    {(kind === "country_region" || kind === "cities") && <div className="combobox-field"><label htmlFor="result-country">{kind === "cities" ? "先选择国家或地区" : "指定国家或地区"}</label>
      <input id="result-country" role="combobox" aria-expanded={countryOptions.length > 0} value={countryQuery} onChange={(event) => { setCountryQuery(event.target.value); setCountry(null); }} />
      {countryOptions.length > 0 && <ul className="combobox-options" role="listbox">{countryOptions.map((item) => <li key={item.countryRegionCode}><button role="option" aria-selected={country?.countryRegionCode === item.countryRegionCode} onClick={() => { setCountry(item); setCountryQuery(item.displayName); setCountryOptions([]); }}>{item.displayName}</button></li>)}</ul>}
    </div>}
    {kind === "cities" && <><div className="combobox-field"><label htmlFor="result-city">查找城市</label>
      <input id="result-city" role="combobox" aria-expanded={cityOptions.length > 0} value={cityQuery} disabled={!country} onChange={(event) => setCityQuery(event.target.value)} />
      {cityOptions.length > 0 && <ul className="combobox-options" role="listbox">{cityOptions.map((item) => <li key={item.placeId}><button role="option" aria-selected={cities.some(({ placeId }) => placeId === item.placeId)} onClick={() => { setCities((current) => current.some(({ placeId }) => placeId === item.placeId) ? current : [...current, item]); setCityQuery(""); setCityOptions([]); }}>{item.displayName}<span>{item.countryDisplayName}</span></button></li>)}</ul>}
    </div><div className="location-tokens">{cities.map((item) => <span key={item.placeId}>{item.displayName}<button aria-label={`移除 ${item.displayName}`} onClick={() => setCities((current) => current.filter(({ placeId }) => placeId !== item.placeId))}>移除</button></span>)}</div></>}
    {message && <p role="status" className="success-message">{message}</p>}{error && <p role="alert" className="error-message">{error}</p>}
    <button className="button secondary" disabled={busy || (kind === "country_region" && !country) || (kind === "cities" && cities.length === 0)} onClick={() => void save()}>{busy ? "Saving" : "保存范围"}</button>
  </section>;
}

export function PairingExperience({ mode, onConfiguration, panel = "explore", onDetailChange, backToken = 0, refreshToken = 0, onCapabilityChange, onRefreshSettled, onBeforeMeetDirtyChange }: { mode: PairingCapabilityMode; onConfiguration: () => void; panel?: "explore" | "connections" | "contacts" | "before-meet" | "result-scope"; onDetailChange?: (detail: boolean) => void; backToken?: number; refreshToken?: number; onCapabilityChange?: () => Promise<void>; onRefreshSettled?: () => void; onBeforeMeetDirtyChange?: (dirty: boolean) => void }) {
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [surface, setSurface] = useState<Surface>(mode === "active" ? "current" : "status");
  const [current, setCurrent] = useState<CurrentCandidate[]>([]);
  const [incoming, setIncoming] = useState<IncomingIntent[]>([]);
  const [status, setStatus] = useState<PairingStatus[]>([]);
  const [clock,setClock] = useState(Date.now);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [connections, setConnections] = useState<ConnectionEntry[]>([]);
  const [contacts, setContacts] = useState<ContactEntry[]>([]);
  const [ownAnswers, setOwnAnswers] = useState<BeforeWeMeetAnswer[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<ConnectionEntry | null>(null);
  const [counterpartContacts, setCounterpartContacts] = useState<ContactEntry[]>([]);
  const [counterpartAnswers, setCounterpartAnswers] = useState<BeforeWeMeetAnswer[]>([]);
  const [newChannel,setNewChannel] = useState<ContactChannel>("wechat");
  const [editingContact, setEditingContact] = useState<ContactEntry | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [beforeDrafts, setBeforeDrafts] = useState<Record<number, string>>({});
  const [beforeSaving, setBeforeSaving] = useState(false);
  const [beforeSaved, setBeforeSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [readFailed,setReadFailed] = useState(false);
  const [readError,setReadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const selectedConnectionId = useRef<string | null>(null);
  const previousMode = useRef(mode);
  const loadEpoch = useRef(0);
  const detailEpoch = useRef(0);

  const clearSelectedConnection = useCallback(() => {
    detailEpoch.current++;
    selectedConnectionId.current = null;
    setSelectedConnection(null);
    setCounterpartContacts([]);
    setCounterpartAnswers([]);
  }, []);

  const readConnectionDetail = useCallback(async (connection: ConnectionEntry) => {
    const [contactResult, beforeResult] = await Promise.all([
      api<{ contacts: ContactEntry[] }>(`/api/v1/pairing/connections/${connection.connectionId}/contacts`),
      api<{ counterpart: BeforeWeMeetAnswer[] }>(`/api/v1/pairing/connections/${connection.connectionId}/before-we-meet`),
    ]);
    return { contacts: contactResult.contacts, answers: beforeResult.counterpart };
  }, []);

  const load = useCallback(async (refreshCandidates = false): Promise<boolean> => {
    const epoch = ++loadEpoch.current;
    let candidateRefreshSucceeded = true;
    try {
      const sync = await api<PairingSyncResult>("/api/v1/pairing/sync", {
        method: "POST",
        headers: { "Idempotency-Key": createIdempotencyKey() },
        body: JSON.stringify({ refreshCandidates }),
      });
      if(sync.capabilityMode !== previousMode.current)await onCapabilityChange?.();
      candidateRefreshSucceeded = sync.candidateRefresh !== "failed";
      if(epoch===loadEpoch.current)setReadError(candidateRefreshSucceeded?"":"Activity 已更新，人选暂时没能刷新，请稍后再试。");
    } catch (caught) {
      candidateRefreshSucceeded = false;
      if(epoch===loadEpoch.current)setReadError(errorMessage(caught));
    }
    const [currentResult, incomingResult, statusResult, historyResult, connectionResult, contactResult, beforeResult] = await Promise.all([
      api<{ candidates: CurrentCandidate[] }>("/api/v1/pairing/current-candidates"),
      api<{ incoming: IncomingIntent[] }>("/api/v1/pairing/incoming"),
      api<{ status: PairingStatus[] }>("/api/v1/pairing/status"),
      api<{ history: HistoryEntry[] }>("/api/v1/pairing/history"),
      api<{ connections: ConnectionEntry[] }>("/api/v1/pairing/connections"),
      api<{ contacts: ContactEntry[] }>("/api/v1/pairing/contacts"),
      api<{ answers: BeforeWeMeetAnswer[] }>("/api/v1/pairing/before-we-meet"),
    ]).catch(caught => {if(epoch===loadEpoch.current){setCurrent([]);setIncoming([]);setStatus([]);setHistory([]);setConnections([]);clearSelectedConnection();setReadFailed(true);setReadError(errorMessage(caught));setLoading(false);}throw caught;});
    if(epoch!==loadEpoch.current)return false;
    setCurrent(currentResult.candidates); setIncoming(incomingResult.incoming); setStatus(statusResult.status);
    setHistory(historyResult.history); setConnections(connectionResult.connections); setContacts(contactResult.contacts);
    setOwnAnswers(beforeResult.answers);setReadFailed(false);setLoading(false);
    const selectedId = selectedConnectionId.current;
    if (selectedId) {
      const nextSelected = connectionResult.connections.find(({ connectionId }) => connectionId === selectedId);
      if (!nextSelected) {
        clearSelectedConnection();
      } else {
        const selectedEpoch = detailEpoch.current;
        try {
          const detail = await readConnectionDetail(nextSelected);
          if(epoch!==loadEpoch.current || selectedEpoch!==detailEpoch.current || selectedConnectionId.current!==selectedId)return candidateRefreshSucceeded;
          setSelectedConnection(nextSelected);
          setCounterpartContacts(detail.contacts);
          setCounterpartAnswers(detail.answers);
        } catch (caught) {
          if(epoch!==loadEpoch.current || selectedEpoch!==detailEpoch.current)return candidateRefreshSucceeded;
          clearSelectedConnection();
          setError(errorMessage(caught));
        }
      }
    }
    return candidateRefreshSucceeded;
  }, [clearSelectedConnection, readConnectionDetail, onCapabilityChange]);
  useEffect(() => { void load(mode === "active").catch((caught) => setReadError(errorMessage(caught))); }, [load]);
  useEffect(() => {
    if (previousMode.current !== mode) {
      previousMode.current = mode;
      void load(true).catch((caught) => setReadError(errorMessage(caught)));
    }
    if (mode !== "active" && (surface === "current" || surface === "incoming")) setSurface("status");
  }, [load, mode, surface]);
  useEffect(() => {
    const next = status.filter(({ state, maturesAt }) => state === "PENDING_WILLING" && Date.parse(maturesAt) > Date.now())
      .sort((a, b) => Date.parse(a.maturesAt) - Date.parse(b.maturesAt))[0];
    if (!next) return;
    const delay = Math.min(Date.parse(next.maturesAt) - Date.now() + 250, 2_147_000_000);
    const timer = window.setTimeout(() => void load(false).catch(caught=>setReadError(errorMessage(caught))), delay);
    return () => window.clearTimeout(timer);
  }, [load, status]);

  useEffect(() => {setNotice("");setError("");setSelectedCandidateId(null);clearSelectedConnection();if(panel==="connections")setSurface("connections");else if(panel==="explore")setSurface(mode==="active"?"current":"status");}, [panel,clearSelectedConnection]);
  useEffect(() => {setSelectedCandidateId(null);clearSelectedConnection();}, [backToken,clearSelectedConnection]);
  useEffect(()=>{if(refreshToken>0)void load(mode === "active" && panel === "explore").catch(caught=>setReadError(errorMessage(caught))).finally(()=>onRefreshSettled?.());},[refreshToken,load]);
  useEffect(() => {
    const refresh = () => {if(document.activeElement?.matches("input,textarea,select"))return;if(document.visibilityState==="visible")void load(false).catch(caught=>setReadError(errorMessage(caught)));};
    window.addEventListener("focus",refresh);document.addEventListener("visibilitychange",refresh);
    return ()=>{window.removeEventListener("focus",refresh);document.removeEventListener("visibilitychange",refresh);};
  }, [load]);
  const beforeDirty = Object.keys(beforeDrafts).length > 0;
  useEffect(() => { onBeforeMeetDirtyChange?.(beforeDirty); }, [beforeDirty, onBeforeMeetDirtyChange]);
  useEffect(() => { if (!beforeSaved) return; const timer = window.setTimeout(() => setBeforeSaved(false), 2400); return () => window.clearTimeout(timer); }, [beforeSaved]);
  useEffect(() => { if (beforeDirty) setBeforeSaved(false); }, [beforeDirty]);
  const selectedCandidate = mode === "active" ? (surface === "incoming" ? incoming : current).find(item=>item.counterpartId===selectedCandidateId) : undefined;
  const selectedPending = Boolean(selectedCandidate && "willingnessState" in selectedCandidate && selectedCandidate.willingnessState === "PENDING_WILLING");
  const selectedPendingStatus = selectedPending ? status.find(item=>item.counterpartId===selectedCandidate?.counterpartId && item.state==="PENDING_WILLING") : undefined;
  const selectedInterest = Boolean(selectedCandidate && !selectedPending && (surface==="incoming" || ("incomingWillingness" in selectedCandidate && selectedCandidate.incomingWillingness)));
  useEffect(()=>{onDetailChange?.(Boolean(selectedCandidate || (panel==="connections"&&selectedConnection)));},[selectedCandidate,selectedConnection,panel,onDetailChange]);

  useEffect(()=>{
    setClock(Date.now());
    if(!status.some(item=>item.state==="PENDING_WILLING"))return;
    const timer=window.setInterval(()=>setClock(Date.now()),30_000);
    return ()=>window.clearInterval(timer);
  },[status]);

  const recordPresentation = useCallback(async (counterpartId: string, idempotencyKey: string): Promise<PresentationOutcome> => {
    try {
      await api(`/api/v1/pairing/candidates/${counterpartId}/impression`, { method: "POST", headers: { "Idempotency-Key": idempotencyKey } });
      return "recorded";
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 404 || caught.status === 409)) {
        setCurrent(rows=>rows.filter(row=>row.counterpartId!==counterpartId));
        setIncoming(rows=>rows.filter(row=>row.counterpartId!==counterpartId));
        setSelectedCandidateId(id=>id===counterpartId?null:id);
        return "stale";
      }
      setError("暂时无法记录这次浏览，正在重试。");
      return "retry";
    }
  }, []);

  const begin = (next: Confirmation) => { if(busy)return; setConfirmation(next); window.setTimeout(() => dialogRef.current?.showModal(), 0); };
  const closeDialog = () => { dialogRef.current?.close(); setConfirmation(null); };
  const runConfirmed = async () => {
    if (!confirmation) return;
    setBusy(true); setError(""); setNotice("");
    try {
      if (confirmation.kind === "delete-contact" && confirmation.entry) {
        await api(`/api/v1/pairing/contacts/${confirmation.entry.entryId}`, { method: "DELETE", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedRevision: confirmation.entry.revision }) });
        setNotice("联系方式已删除，已建立连接的人也无法再在 Perhaps 里看到它。已经复制的内容无法收回。");
      } else if (confirmation.counterpartId) {
        const action = confirmation.kind === "willing" ? "willingness" : confirmation.kind;
        await api(`/api/v1/pairing/${confirmation.kind === "retry" ? "history" : "candidates"}/${confirmation.counterpartId}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() } });
        setNotice(confirmation.kind === "willing" ? "已进入 24 小时考虑期。这段时间内，对方不会收到提示。" : confirmation.kind === "dismiss" ? "已结束这次相遇。" : confirmation.kind === "withdraw" ? "已在考虑期内撤回心意。" : "已重新发起评估，TA 不一定会再出现在 Now。");
      }
      if (confirmation.kind !== "delete-contact") {setSelectedCandidateId(null);setSurface(confirmation.kind === "dismiss" || confirmation.kind === "retry" ? "history" : "status");}
      closeDialog(); await load(false);
    } catch (caught) { setError(errorMessage(caught)); closeDialog(); await load(false).catch(() => undefined); }
    finally { setBusy(false); }
  };

  const openConnection = async (connection: ConnectionEntry) => {
    clearSelectedConnection(); setError("");
    const epoch = detailEpoch.current;
    try {
      const detail = await readConnectionDetail(connection);
      if(epoch!==detailEpoch.current)return;
      selectedConnectionId.current = connection.connectionId;
      setSelectedConnection(connection);
      setCounterpartContacts(detail.contacts);
      setCounterpartAnswers(detail.answers);
    } catch (caught) { if(epoch===detailEpoch.current){clearSelectedConnection();setError(errorMessage(caught));} }
  };

  /** Save every changed Before We Meet answer in one deliberate action. Saving publishes to existing connections,
   *  so answers are never autosaved; unsaved drafts stay in memory across tabs and are guarded on leaving. */
  const saveAllAnswers = async () => {
    const changed = BEFORE_WE_MEET_PROMPTS.map((_, index) => index + 1).filter((promptId) => promptId in beforeDrafts);
    if (!changed.length || beforeSaving) return;
    setBeforeSaving(true); setError(""); setBeforeSaved(false);
    const failed: number[] = [];
    for (const promptId of changed) {
      const existing = ownAnswers.find((answer) => answer.promptId === promptId);
      try {
        await api(`/api/v1/pairing/before-we-meet/${promptId}`, { method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ answerText: beforeDrafts[promptId], expectedRevision: existing?.revision ?? null }) });
        setBeforeDrafts((drafts) => { const next = { ...drafts }; delete next[promptId]; return next; });
      } catch (caught) { failed.push(promptId); setError(`第 ${failed.join("、")} 题没有保存成功（${errorMessage(caught)}）。修改还在页面上，可以再保存一次。`); }
    }
    await load(false).catch(() => undefined);
    setBeforeSaving(false);
    if (!failed.length) { setBeforeSaved(true); setNotice(""); }
  };

  const createContact = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = event.currentTarget; const data = new FormData(form); const channelType = data.get("channelType") as ContactChannel;
    const customLabel = channelType === "other" ? String(data.get("customLabel") ?? "") : null;
    const value = String(data.get("value") ?? ""); setBusy(true); setError("");
    try {
      await api("/api/v1/pairing/contacts", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ channelType, customLabel, value }) });
      form.reset(); await load(false); setNotice("联系方式已保存。Perhaps 不核实号码或账号属于谁，也不会用它来匹配。");
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };

  const updateContact = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingContact) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const customLabel = editingContact.channelType === "other" ? String(data.get("customLabel") ?? "") : null;
    const value = String(data.get("value") ?? "");
    setBusy(true); setError("");
    try {
      await api(`/api/v1/pairing/contacts/${editingContact.entryId}`, {
        method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() },
        body: JSON.stringify({ expectedRevision: editingContact.revision, customLabel, value }),
      });
      setEditingContact(null); await load(false);
      setNotice("联系方式已更新。已建立连接的人下次查看时，会看到新的内容。");
    } catch (caught) { setError(errorMessage(caught)); await load(false).catch(() => undefined); }
    finally { setBusy(false); }
  };

  const heading = panel === "contacts" ? "My Contact Info" : panel === "before-meet" ? "Before We Meet" : panel === "result-scope" ? "Preferred Locations" : { current: "现在遇见的人", incoming: "收到的心意", status: "我的进展", history: "曾经遇见", connections: "已经建立的连接" }[surface];
  const surfaces: ReadonlyArray<readonly [Surface, string]> = mode === "active"
    ? [["current", "Now"], ["incoming", "Interest"], ["status", "Activity"], ["history", "History"]]
    : [["status", "Activity"], ["history", "History"]];
  const modeNotice = mode === "action_required"
    ? "资料或参与设置还需要补全。补全之前不会出现新的人选和心意，History 与 Connections 仍可查看。"
    : mode === "paused"
      ? "匹配已暂停，不会出现新的人选或心意。考虑期内的心意仍可撤回，History 与 Connections 都会保留。"
      : mode === "ended"
        ? "匹配已永久结束，匹配设置不能再修改。History 与 Connections 都会保留。"
        : "";
  if (loading) return <section className="pairing-experience"><LoadingState /></section>;
  if(readFailed && (panel==="explore" || panel==="connections"))return <section className="pairing-empty"><h2>暂时无法读取</h2><p role="alert">{readError}</p><button className="button" onClick={()=>void load(false).catch(caught=>setReadError(errorMessage(caught)))}>Try again</button></section>;
  return <section className={`pairing-experience${panel === "contacts" ? " romantic-contacts" : ""}`} aria-labelledby="pairing-heading">
    <header className="pairing-experience-header"><h2 className="visually-hidden" id="pairing-heading">{heading}</h2>{!onDetailChange&&(selectedCandidate||selectedConnection)&&<button className="text-button" onClick={()=>{setSelectedCandidateId(null);clearSelectedConnection();}}>Back</button>}{!onDetailChange&&<button className="text-button" onClick={() => void load(mode === "active").catch(caught=>setReadError(errorMessage(caught)))}>Refresh</button>}</header>
    {panel==="explore"&&!selectedCandidate&&<nav className="pairing-nav" aria-label="Explore sections" style={{"--seg-count": surfaces.length, "--seg-index": Math.max(0, surfaces.findIndex(([value]) => value === surface))} as React.CSSProperties}>
      {surfaces.map(([value, label]) => <button key={value} aria-current={surface === value ? "page" : undefined} onClick={() => {setSurface(value);setSelectedCandidateId(null);setNotice("");setError("");}}>{label}</button>)}
    </nav>}
    {modeNotice && <p className="quiet-notice">{modeNotice}</p>}
    {(notice || error || readError) && <div className="pairing-feedback" aria-live="polite">{notice && <p>{notice}</p>}{error && <p role="alert" className="error-message">{error}</p>}{readError && <p role="alert" className="error-message">{readError}</p>}</div>}

    {panel === "explore" && mode === "active" && (surface === "current" || surface === "incoming") && <div className="pairing-surface">
      {selectedCandidate ? <PresentedCandidate item={selectedCandidate} onPresented={recordPresentation}>
        {selectedPending ? <p className="r4-state quiet">还在自己的考虑期内，对方尚未收到提示</p>
          : selectedInterest && <p className="r4-state">TA 想进一步认识你</p>}
        <CandidateProfile envelope={selectedCandidate.profile} />
        <div className="r4-decision">
          {selectedPending ? <>{selectedPendingStatus && <ConsiderationClock pendingAt={selectedPendingStatus.pendingAt} maturesAt={selectedPendingStatus.maturesAt} now={clock} />}<div className="encounter-actions"><button className="button" disabled={busy} onClick={()=>{setSelectedCandidateId(null);setSurface("status");}}>查看进展</button><button className="button" disabled={busy} onClick={()=>begin({kind:"dismiss",counterpartId:selectedCandidate.counterpartId})}>结束这次相遇</button></div></>
            : <div className="encounter-actions"><button className="button" disabled={busy} onClick={()=>begin({kind:"willing",counterpartId:selectedCandidate.counterpartId})}>愿意进一步认识 TA</button><button className="button" disabled={busy} onClick={()=>begin({kind:"dismiss",counterpartId:selectedCandidate.counterpartId})}>结束这次相遇</button></div>}
        </div>
      </PresentedCandidate> : (surface === "current" ? current : incoming).length === 0 ? <EmptySurface>{surface==="current"?"No one yet":"Nothing new yet"}</EmptySurface> : (surface === "current" ? current : incoming).map(item=><PresentedCandidate key={item.counterpartId} item={item} onPresented={recordPresentation}>
        <button className="r4-person-entry" aria-label={`查看${item.profile.profile.displayName}的资料`} onClick={()=>setSelectedCandidateId(item.counterpartId)}>
          <div className="r4-person-identity"><span className="r4-monogram" aria-hidden="true">{Array.from(item.profile.profile.displayName)[0]}</span><span><strong>{item.profile.profile.displayName}</strong><small>{AGE_LABELS[item.profile.profile.ageBand]}{item.profile.profile.pronouns ? ` · ${item.profile.profile.pronouns}` : ""}</small></span></div>
          <p>{Array.from(item.profile.profile.shortIntroduction).slice(0,170).join("")}{Array.from(item.profile.profile.shortIntroduction).length>170?"…":""}</p>
        </button>
      </PresentedCandidate>)}
    </div>}

    {panel === "explore" && surface === "status" && <div className="pairing-surface status-list">
      {status.length === 0 ? <EmptySurface>No activity yet</EmptySurface> : status.map((item) => <article className="status-row" key={`${item.counterpartId}-${item.pendingAt}`}>
        <div><h3>{item.profile?.profile.displayName ?? "一位曾经遇见的人"}</h3>{item.state === "PENDING_WILLING" && <><p>{Date.parse(item.maturesAt)>clock?"还在 24 小时考虑期内，对方尚未收到提示。":"考虑期已到，正在确认最新状态。"}</p>{Date.parse(item.maturesAt)>clock&&<ConsiderationClock pendingAt={item.pendingAt} maturesAt={item.maturesAt} now={clock} />}</>}{item.state === "SENT" && <p>心意已送出，接下来由 TA 自己决定。TA 看到了哪些人、设了什么范围、考虑得怎样，你都不会看到。</p>}{item.state === "MUTUAL" && <p>你们都愿意进一步认识，可以在 Connections 里继续了解。</p>}{item.state === "WITHDRAWN" && <p>心意已撤回，对方没有收到过提示。这次相遇仍然保留。</p>}{item.state === "CLOSED" && <p>这次相遇已经结束。</p>}</div>
        {item.state === "PENDING_WILLING" && mode !== "ended" && Date.parse(item.maturesAt) > clock && <div className="encounter-actions"><button className="button" disabled={busy} onClick={() => begin({ kind: "withdraw", counterpartId: item.counterpartId })}>撤回这份心意</button>{mode==="active"&&current.some(candidate=>candidate.counterpartId===item.counterpartId)&&<button className="button" disabled={busy} onClick={()=>begin({kind:"dismiss",counterpartId:item.counterpartId})}>结束这次相遇</button>}</div>}
      </article>)}
    </div>}

    {panel === "explore" && surface === "history" && <div className="pairing-surface history-list">
      {history.length === 0 ? <EmptySurface>No history yet</EmptySurface> : history.map((item) => <article className="history-row" key={item.counterpartId}><div><h3>{item.profile?.profile.displayName ?? "一位曾经遇见的人"}</h3><p>{{ own_closure: "你结束了这次相遇。", encounter_ended: "这次相遇已经结束。", system_update: "系统状态变化后，这次相遇已结束。", natural_expiry: "这次相遇已到期。" }[item.reason]}</p></div>{mode === "active" && item.retryAvailable && <button className="button secondary" disabled={busy} onClick={() => begin({ kind: "retry", counterpartId: item.counterpartId })}>重新考虑一次</button>}</article>)}
    </div>}

    {panel === "connections" && <div className="pairing-surface">
      {connections.length === 0 ? <div className="pairing-empty"><EmptyMark /><h2>No connections yet</h2><p>双方都愿意进一步认识之后，可以在这里说话。</p></div> : !selectedConnection && <div className="connection-list">{connections.map((connection) => <button key={connection.connectionId} className="connection-choice" onClick={() => void openConnection(connection)}><strong>{connection.profile?.profile.displayName ?? "已建立的连接"}</strong><span>建立于 {dateTime(connection.connectedAt)}</span></button>)}</div>}
      {selectedConnection && <section className="connection-detail">{selectedConnection.profile ? <CandidateProfile envelope={selectedConnection.profile} /> : <p className="quiet-notice">对方的资料暂时看不到，连接仍然保留。</p>}<div className="connection-subsection"><h3>TA 分享的联系方式</h3>{counterpartContacts.length === 0 ? <p>对方暂未分享联系方式</p> : <dl className="contact-list">{counterpartContacts.map((entry) => <div key={entry.entryId}><dt>{entry.customLabel ?? CHANNEL_LABELS[entry.channelType]}</dt><dd>{entry.value}</dd></div>)}</dl>}</div><div className="connection-subsection"><h3>TA 在见面前想说的话</h3>{counterpartAnswers.length === 0 ? <p>对方暂时没有填写。</p> : counterpartAnswers.map((answer) => <div className="before-answer" key={answer.promptId}><h4>{BEFORE_WE_MEET_PROMPTS[answer.promptId - 1]}</h4><p>{answer.answerText}</p></div>)}</div></section>}
    </div>}
    {panel === "contacts" && <div className="pairing-surface romantic-contact-surface"><div className="romantic-contact-scroll"><section className="connection-subsection"><h3>我愿意分享的联系方式</h3><p className="section-intro">可以不填，不影响匹配；Perhaps 不核实号码或账号属于谁。只有已建立连接的人能看到；删除后对方无法再查看，已记下或复制的内容无法收回。</p>
        {contacts.length > 0 && <dl className="contact-list">{contacts.map((entry) => <div key={entry.entryId}><dt>{entry.customLabel ?? CHANNEL_LABELS[entry.channelType]}</dt><dd>{entry.value}</dd><span className="contact-actions"><button className="text-button" onClick={() => setEditingContact(entry)}>Edit</button><button className="text-button danger-text" onClick={() => begin({ kind: "delete-contact", entry })}>Delete</button></span></div>)}</dl>}
        {editingContact && <form id="romantic-contact-edit" className="contact-form contact-edit-form" onSubmit={(event) => void updateContact(event)}><p className="form-heading">编辑 {editingContact.customLabel ?? CHANNEL_LABELS[editingContact.channelType]}</p>{editingContact.channelType === "other" && <label>自定义标签<input name="customLabel" maxLength={40} required defaultValue={editingContact.customLabel ?? ""} /></label>}<label>联系方式<input name="value" maxLength={200} required disabled={busy} autoComplete="off" defaultValue={editingContact.value} /></label></form>}
        <form id="romantic-contact-create" hidden={editingContact !== null} className="contact-form contact-create-form" onSubmit={(event) => void createContact(event)}><label>类型<select name="channelType" required value={newChannel} disabled={busy} onChange={event=>setNewChannel(event.target.value as ContactChannel)}>{Object.entries(CHANNEL_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>{newChannel==="other"&&<label>自定义标签<input name="customLabel" maxLength={40} required disabled={busy} /></label>}<label>联系方式<input name="value" maxLength={200} required disabled={busy} autoComplete="off" /></label></form>
      </section></div>
      <div className="romantic-contact-save">{editingContact ? <div className="section-actions"><button className="button secondary" type="button" disabled={busy} onClick={() => setEditingContact(null)}>Cancel</button><button className="button primary" type="submit" form="romantic-contact-edit" disabled={busy} aria-busy={busy || undefined}>{busy ? "Saving" : "Save changes"}</button></div> : <button className="button primary" type="submit" form="romantic-contact-create" disabled={busy} aria-busy={busy || undefined}>{busy ? "Saving" : "保存联系方式"}</button>}</div>
</div>}
    {panel === "before-meet" && <div className="pairing-surface">      <section className="connection-subsection"><h3>见面前想说的话</h3><p className="section-intro">每一题都可以不答，也可以随时修改。这些回答不参与匹配、排序、资格判断或更换人选。建立连接后，对方会看到你保存的全部回答。</p>{BEFORE_WE_MEET_PROMPTS.map((prompt, index) => { const promptId = index + 1; const saved = ownAnswers.find((answer) => answer.promptId === promptId)?.answerText ?? ""; return <BeforeAnswerEditor key={prompt} promptId={promptId} prompt={prompt} value={beforeDrafts[promptId] ?? saved} disabled={beforeSaving} onChange={(text) => setBeforeDrafts((drafts) => { const next = { ...drafts }; if (text === saved) delete next[promptId]; else next[promptId] = text; return next; })} />; })}</section>
        {(beforeDirty || beforeSaving || beforeSaved) && <div className="r4-save-bar" role="status" aria-live="polite"><span>{beforeSaving ? "Saving" : beforeDirty ? `${Object.keys(beforeDrafts).length} 题有未保存的修改` : "Saved"}</span>{(beforeDirty || beforeSaving) && <button className="button primary" disabled={beforeSaving} aria-busy={beforeSaving || undefined} onClick={() => void saveAllAnswers()}>{beforeSaving ? "Saving" : "Save"}</button>}</div>}
</div>}
    {panel === "result-scope" && mode !== "ended" && <ResultScopeEditor onSaved={() => load(true)} />}


    <dialog ref={dialogRef} className="account-dialog" onCancel={event=>{if(busy)event.preventDefault();else closeDialog();}}>{confirmation && <><h2>{confirmation.kind === "willing" ? "愿意进一步认识 TA？" : confirmation.kind === "dismiss" ? "结束这次相遇？" : confirmation.kind === "withdraw" ? "撤回自己的意愿？" : confirmation.kind === "retry" ? "重新评估这次相遇？" : "删除这条联系方式？"}</h2>{confirmation.kind === "willing" ? <><ol className="r4-consequence"><li><strong>现在</strong><span>确认后进入 24 小时考虑期。期间可以撤回，对方不会收到提示。</span></li><li><strong>24 小时后</strong><span>考虑期结束后心意送出，届时不再提供撤回操作。</span></li></ol><p className="dialog-copy">不填联系方式也可以作出选择。</p></> : <p className="dialog-copy">{confirmation.kind === "dismiss" ? "这会结束你这一侧的相遇，不会屏蔽对方。结束的理由不会告知 TA。" : confirmation.kind === "withdraw" ? "考虑期内的心意还没有送出，现在可以撤回。撤回后，这次相遇仍然保留。" : confirmation.kind === "retry" ? "每个人只能重新考虑一次。系统会重新评估，TA 不一定会再出现在 Now。" : "删除后，已建立连接的人也无法再在 Perhaps 里看到它。对方可能已经复制保存。"}</p>}<div className="dialog-actions"><button className="button secondary" disabled={busy} onClick={closeDialog}>Cancel</button><button className="button primary" disabled={busy} aria-busy={busy || undefined} onClick={() => void runConfirmed()}>{busy ? "Processing" : "Confirm"}</button></div></>}</dialog>
  </section>;
}

function BeforeAnswerEditor({ promptId, prompt, value, disabled, onChange }: { promptId: number; prompt: string; value: string; disabled: boolean; onChange: (answer: string) => void }) {
  return <div className="before-editor"><label htmlFor={`before-${promptId}`}>{prompt}</label><textarea id={`before-${promptId}`} value={value} maxLength={1000} rows={3} disabled={disabled} onChange={(event) => onChange(event.target.value)} /></div>;
}
